import type { EndsGoogleBusinessOverview } from '@ballerine/common';

import { ctw } from '@/common';
import { Star } from '../../../../../common/icons/lucide-adapter';
import { ReportWrapper } from '../../../report-shared/components/ReportWrapper';
import { getTagStyles } from '../../../report-shared/sections/TagsSection';
import { HighlightedReviewCard } from './HighlightedReviewCard';
import { formatCount, isAbsoluteHttpUrl } from './utils';

type GoogleBusinessOverviewSectionProps = {
  googleBusinessOverview?: EndsGoogleBusinessOverview | null;
};

const GoogleGlyph = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.46a5.52 5.52 0 0 1-2.4 3.62v3h3.88c2.27-2.09 3.58-5.17 3.58-8.81Z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.96-1.08 7.94-2.91l-3.88-3c-1.08.72-2.45 1.15-4.06 1.15-3.12 0-5.77-2.11-6.71-4.95H1.28v3.09A12 12 0 0 0 12 24Z"
    />
    <path
      fill="#FBBC05"
      d="M5.29 14.29A7.2 7.2 0 0 1 4.91 12c0-.8.14-1.57.38-2.29V6.62H1.28A12 12 0 0 0 0 12c0 1.94.46 3.77 1.28 5.38l4.01-3.09Z"
    />
    <path
      fill="#EA4335"
      d="M12 4.77c1.76 0 3.34.61 4.58 1.79l3.44-3.44C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.28 6.62l4.01 3.09C6.23 6.88 8.88 4.77 12 4.77Z"
    />
  </svg>
);

const StatBlock = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="min-w-32">
    <div className="text-sm font-semibold text-slate-900">{label}</div>
    <div className="mt-1 text-sm">{children}</div>
  </div>
);

export const GoogleBusinessOverviewSection = ({
  googleBusinessOverview,
}: GoogleBusinessOverviewSectionProps) => {
  if (!googleBusinessOverview) {
    return null;
  }

  const {
    rating,
    reviewsCount,
    flaggedCount,
    summary,
    chips,
    highlightedReviews,
    analyzedReviewsUrl,
  } = googleBusinessOverview;

  const isFlagged = (flaggedCount ?? 0) > 0;
  const chipStyles = getTagStyles(isFlagged ? 'negative' : 'positive');
  // A partial imported blob can carry label-less chips; they'd render as empty pills.
  const labelledChips = chips?.filter((chip): chip is { label: string } => chip.label != null);
  const showReviewsLink = analyzedReviewsUrl != null && isAbsoluteHttpUrl(analyzedReviewsUrl);

  return (
    <ReportWrapper
      title={
        <span className="inline-flex items-center gap-2">
          <GoogleGlyph />
          Google Business Overview
        </span>
      }
      showTooltip={false}
    >
      <div className="flex flex-wrap gap-12">
        <StatBlock label="Reviews">
          <span className="text-slate-900">{formatCount(reviewsCount)}</span>
        </StatBlock>
        <StatBlock label="Flagged">
          <span className={ctw(isFlagged ? 'font-semibold text-red-600' : 'text-slate-500')}>
            {formatCount(flaggedCount)}
          </span>
        </StatBlock>
        {rating != null && (
          <StatBlock label="Rating">
            <span className="inline-flex items-center gap-1 text-slate-900">
              {rating}
              <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
            </span>
          </StatBlock>
        )}
      </div>

      {summary && (
        <div className="mt-6">
          <h4 className="mb-2 text-sm font-semibold text-slate-900">Summary</h4>
          <p className="text-sm leading-relaxed text-slate-700">{summary}</p>
        </div>
      )}

      {showReviewsLink && (
        <a
          href={analyzedReviewsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-block text-sm font-medium text-blue-600 underline underline-offset-2 hover:text-blue-800"
        >
          View analyzed Google reviews
        </a>
      )}

      {labelledChips && labelledChips.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2">
          {labelledChips.map((chip, index) => (
            <span
              key={index}
              className={ctw(
                'inline-flex items-center rounded-sm px-1.5 py-0.5 text-xs',
                chipStyles,
              )}
            >
              {chip.label}
            </span>
          ))}
        </div>
      )}

      {highlightedReviews && highlightedReviews.length > 0 && (
        <div className="mt-8">
          <h4 className="mb-3 text-sm font-semibold text-slate-900">Highlighted Reviews</h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {highlightedReviews.map((review, index) => (
              <HighlightedReviewCard key={index} review={review} />
            ))}
          </div>
        </div>
      )}
    </ReportWrapper>
  );
};
